import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Clock, AlertTriangle, ChevronRight, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationPanel = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ urgent: [], active: [], snoozed: [], total_count: 0 });
  const [snoozeId, setSnoozeId] = useState(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setData(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
        setSnoozeId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDismiss = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/dismiss`);
      fetchNotifications();
    } catch { toast.error('Failed to dismiss'); }
  };

  const openSnooze = (notif) => {
    setSnoozeId(notif.id);
    // Default snooze to validity date, fallback to 3 days ahead
    if (notif.validity_date) {
      setSnoozeDate(notif.validity_date.split('T')[0]);
    } else {
      const inThreeDays = new Date();
      inThreeDays.setDate(inThreeDays.getDate() + 3);
      setSnoozeDate(inThreeDays.toISOString().split('T')[0]);
    }
  };

  const handleSnooze = async () => {
    if (!snoozeId || !snoozeDate) return;
    setLoading(true);
    try {
      await axios.post(`${API}/notifications/${snoozeId}/snooze`, { snooze_until: snoozeDate });
      toast.success(`${snoozeDate} tarihine ertelendi`);
      setSnoozeId(null);
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to snooze');
    } finally {
      setLoading(false);
    }
  };

  const goToOffer = (offerId) => {
    setOpen(false);
    navigate(`/offers/${offerId}`);
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const totalCount = data.total_count || 0;
  const hasUrgent = data.urgent?.length > 0;

  const NotifItem = ({ notif, isUrgent, isSnoozed }) => {
    const isSnoozing = snoozeId === notif.id;
    return (
      <div className={`p-3 rounded-lg border ${isUrgent ? 'border-red-200 bg-red-50' : isSnoozed ? 'border-slate-200 bg-slate-50' : 'border-amber-200 bg-amber-50'}`}
        data-testid={`notification-${notif.id}`}>
        <div className="flex items-start justify-between gap-2">
          <button onClick={() => goToOffer(notif.offer_id)} className="text-left flex-1 group" data-testid={`notif-go-${notif.id}`}>
            <p className="text-sm font-medium text-slate-800 group-hover:text-blue-600 transition-colors">
              {notif.customer_name} — {notif.offer_no}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">{notif.message}</p>
            {isSnoozed && notif.snoozed_until && (
              <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                <Clock size={10} /> {notif.snoozed_until.split('T')[0]} tarihine ertelendi
              </p>
            )}
          </button>
          <div className="flex items-center gap-1 flex-shrink-0">
            {!isSnoozed && (
              <button onClick={() => openSnooze(notif)} className="p-1 rounded hover:bg-white/80 text-slate-400 hover:text-blue-600"
                title="Snooze" data-testid={`notif-snooze-btn-${notif.id}`}>
                <Clock size={14} />
              </button>
            )}
            <button onClick={() => handleDismiss(notif.id)} className="p-1 rounded hover:bg-white/80 text-slate-400 hover:text-red-500"
              title="Dismiss" data-testid={`notif-dismiss-${notif.id}`}>
              <X size={14} />
            </button>
          </div>
        </div>

        {isSnoozing && (
          <div className="mt-2 p-2 bg-white rounded border border-slate-200" data-testid="snooze-picker">
            <p className="text-xs text-slate-500 mb-1.5">Tekrar hatırlat:</p>
            <div className="flex items-center gap-2">
              <Input type="date" value={snoozeDate} onChange={e => setSnoozeDate(e.target.value)}
                min={minDate}
                max={notif.validity_date || undefined}
                className="h-8 text-xs flex-1" data-testid="snooze-date-input" />
              <Button size="sm" className="h-8 text-xs" onClick={handleSnooze} disabled={loading}
                data-testid="snooze-confirm-btn">
                {loading ? '...' : 'Ertele'}
              </Button>
              <button onClick={() => setSnoozeId(null)} className="p-1 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={panelRef} className="relative">
      <button onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell">
        <Bell size={20} className={hasUrgent ? 'text-red-500' : 'text-slate-500'} />
        {totalCount > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 w-5 h-5 text-[10px] font-medium text-white rounded-full flex items-center justify-center ${hasUrgent ? 'bg-red-500' : 'bg-amber-500'}`}
            data-testid="notification-badge">
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 max-h-[80vh] overflow-hidden flex flex-col"
          data-testid="notification-panel">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-medium text-lg text-slate-900">Bildirimler</h3>
            <span className="text-xs text-slate-400">{totalCount} aktif</span>
          </div>

          <div className="overflow-y-auto flex-1 p-3 space-y-4">
            {/* Urgent Section */}
            {data.urgent?.length > 0 && (
              <div data-testid="notifications-urgent">
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertTriangle size={14} className="text-red-500" />
                  <span className="text-xs font-medium text-red-600 uppercase">Acil</span>
                  <span className="text-xs text-red-400">({data.urgent.length})</span>
                </div>
                <div className="space-y-2">
                  {data.urgent.map(n => <NotifItem key={n.id} notif={n} isUrgent />)}
                </div>
              </div>
            )}

            {/* Active Section */}
            {data.active?.length > 0 && (
              <div data-testid="notifications-active">
                <div className="flex items-center gap-1.5 mb-2">
                  <Bell size={14} className="text-amber-500" />
                  <span className="text-xs font-medium text-amber-600 uppercase">Aktif</span>
                  <span className="text-xs text-amber-400">({data.active.length})</span>
                </div>
                <div className="space-y-2">
                  {data.active.map(n => <NotifItem key={n.id} notif={n} />)}
                </div>
              </div>
            )}

            {/* Snoozed Section */}
            {data.snoozed?.length > 0 && (
              <div data-testid="notifications-snoozed">
                <div className="flex items-center gap-1.5 mb-2">
                  <Clock size={14} className="text-slate-400" />
                  <span className="text-xs font-medium text-slate-500 uppercase">Ertelenmiş</span>
                  <span className="text-xs text-slate-400">({data.snoozed.length})</span>
                </div>
                <div className="space-y-2">
                  {data.snoozed.map(n => <NotifItem key={n.id} notif={n} isSnoozed />)}
                </div>
              </div>
            )}

            {totalCount === 0 && data.snoozed?.length === 0 && (
              <div className="text-center py-8 text-slate-400" data-testid="no-notifications">
                <Bell size={24} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Bildirim yok</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
